/** Pestaña «Órdenes de compra» de Compras (spec 21): las que esperan entregas primero; un clic abre la orden. */
import { ESTADO_ORDEN_COMPRA_ETIQUETA, type EstadoOrdenCompra } from '@mg/shared';
import { ClipboardList, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useOrdenesCompra } from './api.ts';

const ESTADO_V: Record<EstadoOrdenCompra, 'pendiente' | 'ok' | 'critico' | 'neutro'> = { abierta: 'pendiente', parcial: 'pendiente', completa: 'ok', cerrada: 'neutro', anulada: 'critico' };

export function OrdenesCompra({ puedeRegistrar }: { puedeRegistrar: boolean }) {
  const navigate = useNavigate();
  const [estado, setEstado] = useState('');
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(1);
  const ordenes = useOrdenesCompra({ estado: estado || undefined, q: q || undefined, pagina, limite: 50 });
  return (
    <div className="space-y-3" data-testid="ordenes-compra">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={estado} onChange={(e) => { setEstado(e.target.value); setPagina(1); }} className="w-44" aria-label="Estado de la orden" data-testid="ordenes-estado">
          <option value="">Todos los estados</option>
          {(Object.keys(ESTADO_ORDEN_COMPRA_ETIQUETA) as EstadoOrdenCompra[]).map((e) => <option key={e} value={e}>{ESTADO_ORDEN_COMPRA_ETIQUETA[e]}</option>)}
        </Select>
        <Input placeholder="N° de orden, proveedor o RIF…" value={q} onChange={(e) => { setQ(e.target.value); setPagina(1); }} className="w-72" data-testid="ordenes-buscar" />
        {puedeRegistrar ? <Button asChild data-testid="orden-nueva"><Link to="/compras/ordenes/nueva"><Plus /> Nueva orden de compra</Link></Button> : null}
      </div>
      <EstadoQuery cargando={ordenes.isLoading} error={ordenes.error} datos={ordenes.data} reintentar={() => void ordenes.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio icono={<ClipboardList className="h-6 w-6" />} titulo="Sin órdenes de compra" descripcion="Pide una cotización al proveedor con «Nueva orden de compra»: propone lo que está bajo el mínimo." />
          ) : (
            <>
              <Table>
                <TableHeader><TableRow><TableHead>Orden</TableHead><TableHead>Proveedor</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Productos</TableHead><TableHead>Llega a</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.datos.map((o) => (
                    <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/compras/ordenes/${o.id}`)} data-testid={`orden-${o.numero_formateado}`}>
                      <TableCell className="font-medium">{o.numero_formateado}</TableCell>
                      <TableCell>{o.proveedor_razon_social}<span className="block text-[11px] text-muted-foreground">{o.proveedor_rif}</span></TableCell>
                      <TableCell className="text-xs">{formatearFechaHora(o.creado_en)}<span className="block text-muted-foreground">{o.creado_por_nombre}{o.enviada_en ? ' · enviada por correo' : ''}</span></TableCell>
                      <TableCell className="text-right num">{o.lineas}</TableCell>
                      <TableCell className="text-xs">{o.almacen_nombre ?? '—'}</TableCell>
                      <TableCell><Badge variant={ESTADO_V[o.estado]}>{ESTADO_ORDEN_COMPRA_ETIQUETA[o.estado]}</Badge>{o.entregas > 0 ? <span className="block text-[11px] text-muted-foreground">{o.entregas === 1 ? '1 entrega' : `${o.entregas} entregas`}</span> : null}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {d.total > d.datos.length ? (
                <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
                  <span>{(pagina - 1) * d.limite + 1}–{(pagina - 1) * d.limite + d.datos.length} de {d.total}</span>
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                  <Button size="sm" variant="outline" disabled={pagina * d.limite >= d.total} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
                </div>
              ) : null}
            </>
          )
        }
      </EstadoQuery>
    </div>
  );
}
